import { toast as ckToast, ToastRegion, type ToastStatus } from '@main/complykit/components/toast';
import { ReactNode, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/* The old wrapper's option names on ComplyKit's one queue, so no caller moved. */

export type ToastId = string;

export interface ToastOptions {
  /** A stable id: a second toast with the same id updates the first instead of stacking. */
  id?: ToastId;
  title?: ReactNode;
  description?: ReactNode;
  status?: ToastStatus;
  /** Milliseconds, or `null` to stay until dismissed. Defaults to 5000, or `null` while loading. */
  duration?: number | null;
  /** @deprecated Every toast is dismissible; accepted and ignored. */
  isClosable?: boolean;
  onCloseComplete?: () => void;
  /** One thing the user can do about it; an actionable toast stays until pressed or dismissed. */
  action?: { label: string; onPress: () => void };
}

const contentOf = ({ title, description, status, action }: ToastOptions) => ({
  status: status ?? 'info',
  title: title ?? '',
  description,
  action,
});

function show(options: ToastOptions): ToastId {
  return ckToast.show(contentOf(options), {
    id: options.id,
    duration: options.duration,
    onClose: options.onCloseComplete,
  }).id;
}

export const toast = Object.assign(show, {
  update: (id: ToastId, options: Partial<ToastOptions>) =>
    ckToast.update(id, {
      ...(options.status ? { status: options.status } : {}),
      ...('title' in options ? { title: options.title ?? '' } : {}),
      ...('description' in options ? { description: options.description } : {}),
      ...('action' in options ? { action: options.action } : {}),
      ...(options.duration !== undefined ? { duration: options.duration } : {}),
    }),
  close: (id: ToastId) => ckToast.close(id),
  isActive: (id: ToastId) => ckToast.isActive(id),
  closeAll: () => ckToast.closeAll(),
});

/** Mounted once per app root; renders whatever the queue holds. */
export const ToastContainer = () => {
  const { t } = useTranslation('ui');
  return <ToastRegion label={t('toast.region')} dismissLabel={t('toast.dismiss')} />;
};

export const errorToast = (title: ReactNode) => toast({ title, status: 'error' });

export const successToast = (title: ReactNode, description?: ReactNode) =>
  toast({ title, description, status: 'success' });

export const infoToast = (title: ReactNode, description?: ReactNode) =>
  toast({ title, description, status: 'info' });

/** One toast that walks loading, then success or error, updated in place. */
export function useMultiStateToast(globalOptions?: Omit<ToastOptions, 'id'>) {
  const toastIdRef = useRef<ToastId | null>(null);

  const loading = useCallback(
    (options: Omit<ToastOptions, 'id'>) => {
      toastIdRef.current = toast({
        status: 'loading',
        duration: null,
        ...globalOptions,
        ...options,
      });
      return toastIdRef.current;
    },
    [globalOptions],
  );

  const success = useCallback(
    (options: Omit<ToastOptions, 'id'>) => {
      if (toastIdRef.current) {
        toast.update(toastIdRef.current, {
          status: 'success',
          duration: 10000,
          ...globalOptions,
          ...options,
        });
      }
    },
    [globalOptions],
  );

  const error = useCallback(
    (options: Omit<ToastOptions, 'id'>) => {
      if (toastIdRef.current) {
        toast.update(toastIdRef.current, {
          status: 'error',
          duration: 5000,
          ...globalOptions,
          ...options,
        });
      }
    },
    [globalOptions],
  );

  const close = useCallback(() => {
    if (toastIdRef.current) toast.close(toastIdRef.current);
  }, []);

  const update = useCallback((options: Omit<ToastOptions, 'id'>) => {
    if (toastIdRef.current) toast.update(toastIdRef.current, options);
  }, []);

  return { loading, success, error, close, update };
}
